import React, { useEffect, useState } from "react";
import { Search, SlidersHorizontal, ArrowUp, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { COUNTRIES, FLAGS } from "@/lib/utils";
import PropertyCard from "@/components/PropertyCard";

const TAGS = ["Off-plan", "Ready", "Luxury", "Waterfront", "Smart Home", "Gated", "High-rise", "Premium", "Rental Ready"];

export default function Discover() {
  const [devs, setDevs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("all");
  const [tag, setTag] = useState("all");
  const [sort, setSort] = useState("newest");

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, country, tag, sort]);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (country !== "all") params.append("country", country);
      if (tag !== "all") params.append("tag", tag);
      params.append("sort", sort);
      const { data } = await api.get(`/developments?${params.toString()}`);
      setDevs(data);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-[11px] sm:text-xs uppercase tracking-[0.18em] text-neutral-500 font-semibold">Discover</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900 mt-2">
            Verified developments
          </h1>
          <p className="text-sm sm:text-base text-neutral-500 mt-2">Curated developments across <span className="text-propera font-semibold">Africa</span> · diaspora-friendly payment plans</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="filter-sort"
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="px-4 py-2.5 rounded-full bg-white border border-neutral-200 text-sm font-medium focus:border-propera focus:ring-2 focus:ring-propera/15 outline-none"
          >
            <option value="newest">Newest</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
          </select>
          <button
            type="button"
            data-testid="sort-direction-toggle"
            title={sort === "price_high" ? "Switch to Low → High" : sort === "price_low" ? "Switch to High → Low" : "Sort by price: Low → High"}
            onClick={() => {
              if (sort === "price_high") setSort("price_low");
              else if (sort === "price_low") setSort("price_high");
              else setSort("price_low");
            }}
            className={`w-10 h-10 rounded-full flex items-center justify-center border transition ${sort === "price_high" || sort === "price_low" ? "bg-propera text-white border-propera hover:bg-propera-hover" : "bg-white border-neutral-200 text-neutral-500 hover:border-neutral-300"}`}
            aria-label="Toggle sort direction"
          >
            {sort === "price_high" ? <ArrowDown size={16} /> : <ArrowUp size={16} />}
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="mt-8 soft-card p-2 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            data-testid="discover-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, city, or description..."
            className="w-full pl-11 pr-4 py-3 rounded-xl bg-transparent focus:outline-none text-sm"
          />
        </div>
        <SlidersHorizontal size={16} className="text-neutral-400 mx-1 hidden sm:block" />
      </div>

      {/* Country pills */}
      <div className="mt-4 -mx-6 lg:mx-0 px-6 lg:px-0 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 min-w-max pb-1">
          <CountryPill active={country === "all"} onClick={() => setCountry("all")} testId="country-all">All</CountryPill>
          {["Nigeria", "Ghana", "Kenya"].map((c) => (
            <CountryPill key={c} active={country === c} onClick={() => setCountry(c)} testId={`country-${c}`}>
              {FLAGS[c]} {c}
            </CountryPill>
          ))}
        </div>
      </div>

      {/* Tags */}
      <div className="mt-3 -mx-6 lg:mx-0 px-6 lg:px-0 overflow-x-auto no-scrollbar">
        <div className="flex gap-2 min-w-max pb-1">
          <TagPill active={tag === "all"} onClick={() => setTag("all")} testId="tag-all">All tags</TagPill>
          {TAGS.map((t) => (
            <TagPill key={t} active={tag === t} onClick={() => setTag(t)} testId={`tag-${t}`}>{t}</TagPill>
          ))}
        </div>
      </div>

      {/* Grid */}
      <div className="mt-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="aspect-[4/3] rounded-3xl bg-neutral-100 animate-pulse" />)}
          </div>
        ) : devs.length === 0 ? (
          <div className="text-center py-20" data-testid="discover-empty">
            {(country === "all" && tag === "all" && !search) ? (
              <>
                <p className="text-lg font-semibold text-neutral-900">No developments listed yet</p>
                <p className="text-sm text-neutral-500 mt-2 max-w-md mx-auto">We&apos;re onboarding developers across Africa right now. Check back soon — the first verified developments will appear here.</p>
              </>
            ) : (
              <p className="text-neutral-500">No developments match your filters.</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {devs.map((dev) => <PropertyCard key={dev.id} dev={dev} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function CountryPill({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-4 py-2 rounded-full text-sm font-medium transition border ${
        active
          ? "bg-propera text-white border-propera"
          : "bg-white text-neutral-700 border-neutral-200 hover:border-neutral-300"
      }`}
    >
      {children}
    </button>
  );
}

function TagPill({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${
        active
          ? "bg-neutral-900 text-white"
          : "bg-white text-neutral-600 border border-neutral-200 hover:border-neutral-300"
      }`}
    >
      {children}
    </button>
  );
}
